import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Layout } from "views/common/Layout";
import { Link, useHistory, useParams, useLocation } from "react-router-dom";
import { allPosts } from "@content-collections/generated";

const BlogCard = ({ post }) => {
    console.log(post);
    return (
        <Link to={`/blog-post/${post._meta.path}`} className="group">
            <article className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:shadow-lg">
                <div className="aspect-video overflow-hidden">
                    <img
                        src={post.image}
                        alt={post.imageAlt}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                        crossOrigin={
                            process.env.NODE_ENV === "development"
                                ? "anonymous"
                                : ""
                        }
                    />
                </div>
                <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{post.category}</span>
                        <span>•</span>
                        <span>{post.date}</span>
                    </div>
                    <h2 className="mt-4 text-xl font-semibold text-gray-900 group-hover:text-blue-600">
                        {post.title}
                    </h2>
                    <p className="mt-2 flex-1 text-gray-600">{post.summary}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {post.tags?.map((tag, index) => (
                            <span
                                key={index}
                                className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                    <div className="mt-6 flex items-center gap-4">
                        <div className="text-sm text-gray-500">
                            {/*<span className="font-medium text-gray-900">
                                {post.author}
                            </span>
                            <span className="mx-2">•</span>*/}
                            <span>{post.date}</span>
                        </div>
                    </div>
                </div>
            </article>
        </Link>
    );
};

export const Blog = () => {
    const POSTS_PER_PAGE = 6;
    const history = useHistory();
    const { page } = useParams();
    const location = useLocation();

    // Convert page param to number, default to 1 if invalid
    const currentPage = parseInt(page) || 1;

    const allTags = [
        ...new Set(allPosts.flatMap((post) => post.tags || [])),
    ].join(", ");
    const categories = [...new Set(allPosts.map((post) => post.category))].join(
        ", "
    );

    // Calculate pagination
    const totalPages = Math.ceil(allPosts.length / POSTS_PER_PAGE);
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    const endIndex = startIndex + POSTS_PER_PAGE;
    const sortedPosts = allPosts.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
    );
    const currentPosts = sortedPosts.slice(startIndex, endIndex);

    // Redirect to page 1 if current page is invalid
    React.useEffect(() => {
        if (currentPage < 1 || currentPage > totalPages) {
            history.replace("/blog/1");
        }
    }, [currentPage, totalPages, history]);

    // Update canonical URL and meta tags for each page
    const pageUrl = `${window.location.origin}/blog/${currentPage}`;
    const pageTitle =
        currentPage === 1
            ? "Product Insights & Updates | Launch Hero FYI Blog"
            : `Page ${currentPage} | Product Insights & Updates | Launch Hero FYI Blog`;

    return (
        <Layout>
            <Helmet>
                <title>{pageTitle}</title>
                <meta
                    name="description"
                    content="Discover the latest trends, insights, and best practices in product development and management. Stay updated with our comprehensive collection of articles on AI tools, software development, and technology trends."
                />
                <meta
                    name="keywords"
                    content={`${allTags}, ${categories}, product insights, AI tools, software development, technology trends, product management`}
                />
                <meta property="og:title" content={pageTitle} />
                <meta
                    property="og:description"
                    content="Discover the latest trends, insights, and best practices in product development and management. Stay updated with our comprehensive collection of articles on AI tools, software development, and technology trends."
                />
                <meta property="og:type" content="blog" />
                <meta property="og:url" content={pageUrl} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta
                    name="twitter:description"
                    content="Discover the latest trends, insights, and best practices in product development and management. Stay updated with our comprehensive collection of articles on AI tools, software development, and technology trends."
                />
                <link rel="canonical" href={pageUrl} />
            </Helmet>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="mx-auto max-w-2xl text-center">
                    <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                        Product Insights & Updates
                    </h1>
                    <p className="mt-4 text-lg text-gray-600">
                        Discover the latest trends, insights, and best practices
                        in product development and management.
                    </p>
                </div>
                <div className="mx-auto mt-16 grid max-w-7xl gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {currentPosts.map((post) => (
                        <BlogCard key={post._meta.path} post={post} />
                    ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="mt-12 flex justify-center">
                        <nav
                            className="flex items-center gap-2"
                            aria-label="Pagination"
                        >
                            <Link
                                to={`/blog/${currentPage - 1}`}
                                onClick={(e) => {
                                    if (currentPage === 1) {
                                        e.preventDefault();
                                    }
                                }}
                                className={`relative inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                                    currentPage === 1
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                }`}
                                aria-disabled={currentPage === 1}
                            >
                                Previous
                            </Link>

                            {[...Array(totalPages)].map((_, index) => {
                                const pageNum = index + 1;
                                const shouldShow =
                                    pageNum === 1 ||
                                    pageNum === totalPages ||
                                    Math.abs(pageNum - currentPage) <= 1;

                                if (!shouldShow) {
                                    if (
                                        pageNum === 2 ||
                                        pageNum === totalPages - 1
                                    ) {
                                        return (
                                            <span
                                                key={pageNum}
                                                className="px-3 py-2 text-sm text-gray-700"
                                            >
                                                ...
                                            </span>
                                        );
                                    }
                                    return null;
                                }

                                return (
                                    <Link
                                        key={pageNum}
                                        to={`/blog/${pageNum}`}
                                        className={`relative inline-flex items-center rounded-md px-3 py-2 text-sm font-medium ${
                                            currentPage === pageNum
                                                ? "bg-blue-600 text-white"
                                                : "text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        {pageNum}
                                    </Link>
                                );
                            })}

                            <Link
                                to={`/blog/${currentPage + 1}`}
                                onClick={(e) => {
                                    if (currentPage === totalPages) {
                                        e.preventDefault();
                                    }
                                }}
                                className={`relative inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                                    currentPage === totalPages
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                }`}
                                aria-disabled={currentPage === totalPages}
                            >
                                Next
                            </Link>
                        </nav>
                    </div>
                )}
            </div>
        </Layout>
    );
};
